import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Switch, FormControlLabel, Grid, Divider, Alert, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as LocalOfferIcon,
  LocalShipping as LocalShippingIcon,
  VerifiedUser as VerifiedUserIcon,
  Support as SupportIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

// MUI 圖示對照表
const iconMap = {
  LocalOffer: LocalOfferIcon,
  LocalShipping: LocalShippingIcon,
  VerifiedUser: VerifiedUserIcon,
  Support: SupportIcon
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 付款方式
  const [paymentMethods, setPaymentMethods] = useState([]);

  // SmilePay 設定
  const [smilepayConfig, setSmilepayConfig] = useState({
    dcvc: '',
    rvg2c_key: '',
    rot_url: '',
    pay_end_url: '',
    test_mode: true
  });

  // 聯合信用卡中心設定
  const [ncccConfig, setNcccConfig] = useState({
    merchant_id: '',
    terminal_id: '',
    hash_key: '',
    hash_iv: '',
    test_mode: true
  });

  // LINE Pay 設定
  const [linepayConfig, setLinepayConfig] = useState({
    channel_id: '',
    channel_secret: '',
    test_mode: true
  });

  // 運費設定
  const [shippingConfig, setShippingConfig] = useState({
    base_fee: 60,
    free_shipping_threshold: 0
  });

  // 行銷追蹤設定
  const [marketingConfig, setMarketingConfig] = useState({
    gtm_id: '',
    ga4_id: '',
    google_ads_id: '',
    google_ads_label: '',
    fb_pixel_id: '',
    fb_access_token: '',
    tiktok_pixel_id: '',
    tiktok_access_token: '',
    tiktok_test_mode: false,
    google_search_console: '',
    merchant_center_id: '',
    merchant_feed_enabled: false
  });

  // 登入方式設定
  const [authConfig, setAuthConfig] = useState({
    phone_enabled: true,
    google_enabled: false,
    google_client_id: '',
    google_client_secret: '',
    line_enabled: false,
    line_channel_id: '',
    line_channel_secret: '',
    line_callback_url: ''
  });

  // 商店資訊設定
  const [storeConfig, setStoreConfig] = useState({
    store_name: '',
    store_name_short: '',
    store_name_en: '',
    company_name: '',
    phone: '',
    email: '',
    line_id: '',
    service_hours: '',
    logo_url: '',
    favicon_url: '',
    og_image_url: '',
    description: '',
    slogan: '',
    stats_inventory: '',
    stats_delivery: '',
    facebook_url: '',
    instagram_url: '',
    line_url: ''
  });

  // SEO 設定
  const [seoConfig, setSeoConfig] = useState({
    site_name: '',
    site_url: '',
    default_title: '',
    default_description: '',
    default_keywords: '',
    google_verification: '',
    bing_verification: ''
  });

  // 首頁特色設定
  const [featuresConfig, setFeaturesConfig] = useState({
    feature1_icon: 'LocalOffer',
    feature1_title: '',
    feature1_desc: '',
    feature2_icon: 'LocalShipping',
    feature2_title: '',
    feature2_desc: '',
    feature3_icon: 'VerifiedUser',
    feature3_title: '',
    feature3_desc: '',
    feature4_icon: 'Support',
    feature4_title: '',
    feature4_desc: ''
  });

  // 電子發票設定
  const [einvoiceConfig, setEinvoiceConfig] = useState({
    grvc: '',
    verify_key: '',
    intype: '07',
    tax_type: '1',
    test_mode: false,
    print_enabled: false,
    auto_issue: false,
    default_carrier: 'EJ0113'
  });

  // 圖片上傳參考
  const logoInputRef = React.useRef(null);
  const faviconInputRef = React.useRef(null);
  const ogImageInputRef = React.useRef(null);

  useEffect(() => {
    loadAllSettings();
    // 從路由 state 取得 tab 參數
    if (location.state?.tab !== undefined) {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const [paymentRes, smilepayRes, ncccRes, linepayRes, shippingRes, marketingRes, authRes, storeRes, seoRes, featuresRes, einvoiceRes] = await Promise.all([
        api.get('/settings/payment-methods'),
        api.get('/settings/smilepay'),
        api.get('/settings/nccc'),
        api.get('/settings/linepay'),
        api.get('/settings/shipping'),
        api.get('/settings/marketing'),
        api.get('/settings/auth'),
        api.get('/settings/store'),
        api.get('/settings/seo'),
        api.get('/settings/features'),
        api.get('/settings/einvoice')
      ]);

      if (paymentRes.data.success) setPaymentMethods(paymentRes.data.payment_methods);
      if (smilepayRes.data.success) setSmilepayConfig(prev => ({ ...prev, ...smilepayRes.data.config, test_mode: smilepayRes.data.config.test_mode === '1' }));
      if (ncccRes.data.success) setNcccConfig(prev => ({ ...prev, ...ncccRes.data.config, test_mode: ncccRes.data.config.test_mode === '1' }));
      if (linepayRes.data.success) setLinepayConfig(prev => ({ ...prev, ...linepayRes.data.config, test_mode: linepayRes.data.config.test_mode === '1' }));
      if (shippingRes.data.success) setShippingConfig(prev => ({
        ...prev,
        base_fee: parseInt(shippingRes.data.config.base_fee) || 60,
        free_shipping_threshold: parseInt(shippingRes.data.config.free_shipping_threshold) || 0
      }));
      if (marketingRes.data.success) setMarketingConfig(prev => ({
        ...prev,
        ...marketingRes.data.config,
        merchant_feed_enabled: marketingRes.data.config.merchant_feed_enabled === '1',
        tiktok_test_mode: marketingRes.data.config.tiktok_test_mode === '1'
      }));
      if (authRes.data.success) setAuthConfig(prev => ({
        ...prev,
        ...authRes.data.config,
        phone_enabled: authRes.data.config.phone_enabled === '1' || authRes.data.config.phone_enabled === true,
        google_enabled: authRes.data.config.google_enabled === '1' || authRes.data.config.google_enabled === true,
        line_enabled: authRes.data.config.line_enabled === '1' || authRes.data.config.line_enabled === true
      }));
      if (storeRes.data.success) setStoreConfig(prev => ({ ...prev, ...storeRes.data.config }));
      if (seoRes.data.success) setSeoConfig(prev => ({ ...prev, ...seoRes.data.config }));
      if (featuresRes.data.success) setFeaturesConfig(prev => ({ ...prev, ...featuresRes.data.config }));
      if (einvoiceRes.data.success) setEinvoiceConfig(prev => ({
        ...prev,
        ...einvoiceRes.data.config,
        test_mode: einvoiceRes.data.config.test_mode === '1',
        print_enabled: einvoiceRes.data.config.print_enabled === '1',
        auto_issue: einvoiceRes.data.config.auto_issue === '1'
      }));
    } catch (error) {
      toast.error('載入設定失敗');
    } finally {
      setLoading(false);
    }
  };

  // 圖片上傳處理
  const handleImageUpload = async (file, fieldName) => {
    if (!file) return;

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      toast.error('請選擇圖片檔案');
      return;
    }

    // 檢查檔案大小 (最大 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('圖片大小不可超過 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', fieldName);

    try {
      const response = await api.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setStoreConfig(prev => ({ ...prev, [fieldName]: response.data.url }));
        toast.success('圖片上傳成功');
      }
    } catch (error) {
      // 如果 API 不存在，使用 base64 暫存
      const reader = new FileReader();
      reader.onloadend = () => {
        setStoreConfig(prev => ({ ...prev, [fieldName]: reader.result }));
        toast.info('圖片已選擇（需儲存後生效）');
      };
      reader.readAsDataURL(file);
    }
  };

  // 切換付款方式啟用狀態
  const togglePaymentMethod = async (id, isActive) => {
    try {
      await api.put(`/settings/payment-methods/${id}`, { is_active: isActive ? 1 : 0 });
      setPaymentMethods(prev =>
        prev.map(m => m.id === id ? { ...m, is_active: isActive ? 1 : 0 } : m)
      );
      toast.success('已更新');
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  // 調整排序
  const movePaymentMethod = async (id, direction) => {
    const index = paymentMethods.findIndex(m => m.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === paymentMethods.length - 1)) return;

    const newMethods = [...paymentMethods];
    const temp = newMethods[index];
    newMethods[index] = newMethods[index + direction];
    newMethods[index + direction] = temp;

    // 更新 sort_order
    const items = newMethods.map((m, i) => ({ id: m.id, sort_order: i + 1 }));

    try {
      await api.put('/settings/payment-methods/batch/sort', { items });
      setPaymentMethods(newMethods.map((m, i) => ({ ...m, sort_order: i + 1 })));
      toast.success('排序已更新');
    } catch (error) {
      toast.error('更新排序失敗');
    }
  };

  // 儲存 SmilePay 設定
  const saveSmilepayConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/smilepay', smilepayConfig);
      toast.success('SmilePay 設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存 NCCC 設定
  const saveNcccConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/nccc', ncccConfig);
      toast.success('聯合信用卡設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存 LINE Pay 設定
  const saveLinepayConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/linepay', linepayConfig);
      toast.success('LINE Pay 設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存運費設定
  const saveShippingConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/shipping', shippingConfig);
      toast.success('運費設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存行銷追蹤設定
  const saveMarketingConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/marketing', marketingConfig);
      toast.success('行銷追蹤設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存登入方式設定
  const saveAuthConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/auth', authConfig);
      toast.success('登入方式設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存商店設定
  const saveStoreConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/store', storeConfig);
      toast.success('商店資訊已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存 SEO 設定
  const saveSeoConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/seo', seoConfig);
      toast.success('SEO 設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存首頁特色設定
  const saveFeaturesConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/features', featuresConfig);
      toast.success('首頁特色設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 儲存電子發票設定
  const saveEinvoiceConfig = async () => {
    setSaving(true);
    try {
      await api.put('/settings/einvoice', einvoiceConfig);
      toast.success('電子發票設定已儲存');
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const paymentMethodLabels = {
    cod: '取貨付款',
    credit: '信用卡',
    linepay: 'LINE Pay',
    smilepay: '超商代碼繳費',
    atm: 'ATM 轉帳'
  };

  return (
    <Box sx={{ maxWidth: '100%', p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          系統設定
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadAllSettings} disabled={loading}>
          重新載入
        </Button>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="付款方式" />
          <Tab label="SmilePay 金物流" />
          <Tab label="電子發票" />
          <Tab label="聯合信用卡中心" />
          <Tab label="LINE Pay" />
          <Tab label="運費設定" />
          <Tab label="行銷追蹤" />
          <Tab label="登入設定" />
          <Tab label="商店資訊" />
          <Tab label="SEO 設定" />
          <Tab label="首頁特色" />
        </Tabs>
      </Paper>

      {/* 付款方式 */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>付款方式管理</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            拖曳調整順序，開關切換是否啟用。啟用的付款方式會顯示在結帳頁面。
          </Alert>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={80}>排序</TableCell>
                  <TableCell>付款方式</TableCell>
                  <TableCell>說明</TableCell>
                  <TableCell width={100} align="center">狀態</TableCell>
                  <TableCell width={120} align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentMethods.map((method, index) => (
                  <TableRow key={method.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => movePaymentMethod(method.id, -1)} disabled={index === 0}>
                          <UpIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => movePaymentMethod(method.id, 1)} disabled={index === paymentMethods.length - 1}>
                          <DownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {method.logo_url ? (
                          <img src={method.logo_url} alt={method.name} style={{ height: 20 }} />
                        ) : method.icon}
                        <Typography fontWeight="bold">{method.name}</Typography>
                        <Typography variant="caption" color="text.secondary">({method.code})</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{method.description}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={method.is_active ? '啟用' : '停用'}
                        color={method.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={!!method.is_active}
                        onChange={(e) => togglePaymentMethod(method.id, e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* SmilePay 設定 */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>SmilePay 金物流設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            SmilePay 金物流整合超商代碼繳費、ATM 虛擬帳號、超商取貨付款等服務。
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="商家代碼 (Dcvc)"
                value={smilepayConfig.dcvc}
                onChange={(e) => setSmilepayConfig(prev => ({ ...prev, dcvc: e.target.value }))}
                helperText="SmilePay 提供的商家代碼"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="驗證金鑰 (Rvg2c_key)"
                value={smilepayConfig.rvg2c_key}
                onChange={(e) => setSmilepayConfig(prev => ({ ...prev, rvg2c_key: e.target.value }))}
                helperText="SmilePay 提供的驗證金鑰"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="付款回調網址 (ROT URL)"
                value={smilepayConfig.rot_url}
                onChange={(e) => setSmilepayConfig(prev => ({ ...prev, rot_url: e.target.value }))}
                placeholder="https://yoursite.com/api/smilepay/callback"
                helperText="SmilePay 付款完成後的回調網址"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="付款完成頁面 (Pay End URL)"
                value={smilepayConfig.pay_end_url}
                onChange={(e) => setSmilepayConfig(prev => ({ ...prev, pay_end_url: e.target.value }))}
                placeholder="https://yoursite.com/checkout/complete"
                helperText="消費者付款後的跳轉頁面"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smilepayConfig.test_mode}
                    onChange={(e) => setSmilepayConfig(prev => ({ ...prev, test_mode: e.target.checked }))}
                  />
                }
                label="測試模式 (不實際扣款)"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveSmilepayConfig}
                disabled={saving}
              >
                儲存 SmilePay 設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 電子發票設定 */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>SmilePay 電子發票設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            電子發票由 SmilePay 代開，支援手機條碼載具、自然人憑證、捐贈發票等功能。
          </Alert>

          <Grid container spacing={3}>
            {/* API 設定 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                API 設定
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="發票商家代碼 (Grvc)"
                value={einvoiceConfig.grvc}
                onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, grvc: e.target.value }))}
                helperText="SmilePay 提供的發票商家代碼"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="發票驗證金鑰 (Verify_key)"
                value={einvoiceConfig.verify_key}
                onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, verify_key: e.target.value }))}
                type="password"
                helperText="SmilePay 提供的發票驗證金鑰"
              />
            </Grid>

            {/* 發票設定 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                發票設定
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="發票類別 (Intype)"
                value={einvoiceConfig.intype}
                onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, intype: e.target.value }))}
                SelectProps={{ native: true }}
                helperText="07: 一般稅額, 08: 特種稅額"
              >
                <option value="07">07 - 一般稅額</option>
                <option value="08">08 - 特種稅額</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="課稅別 (Tax_type)"
                value={einvoiceConfig.tax_type}
                onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, tax_type: e.target.value }))}
                SelectProps={{ native: true }}
                helperText="1: 應稅, 2: 零稅率, 3: 免稅"
              >
                <option value="1">1 - 應稅</option>
                <option value="2">2 - 零稅率</option>
                <option value="3">3 - 免稅</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="預設載具類型"
                value={einvoiceConfig.default_carrier}
                onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, default_carrier: e.target.value }))}
                SelectProps={{ native: true }}
                helperText="若未指定載具時的預設選項"
              >
                <option value="EJ0113">速買配載具 (EJ0113)</option>
                <option value="3J0002">手機條碼 (3J0002)</option>
                <option value="CQ0001">自然人憑證 (CQ0001)</option>
              </TextField>
            </Grid>

            {/* 功能開關 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                功能開關
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={einvoiceConfig.test_mode}
                    onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, test_mode: e.target.checked }))}
                  />
                }
                label="測試模式"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                啟用後使用測試環境，不會實際開立發票
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={einvoiceConfig.print_enabled}
                    onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, print_enabled: e.target.checked }))}
                  />
                }
                label="允許列印紙本"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                是否允許消費者選擇列印紙本發票
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={einvoiceConfig.auto_issue}
                    onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, auto_issue: e.target.checked }))}
                  />
                }
                label="自動開立發票"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                訂單付款完成後自動開立發票
              </Typography>
            </Grid>

            {/* 載具類型說明 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>載具類型說明</Typography>
                <Typography variant="body2">
                  • <strong>速買配載具 (EJ0113)</strong>：發票存入 SmilePay 會員載具<br/>
                  • <strong>手機條碼 (3J0002)</strong>：發票存入手機條碼載具，格式為 /XXXXXXX<br/>
                  • <strong>自然人憑證 (CQ0001)</strong>：發票存入自然人憑證載具，格式為 2碼英文+14碼數字
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveEinvoiceConfig}
                disabled={saving}
              >
                儲存電子發票設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 聯合信用卡中心設定 */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>聯合信用卡中心 (NCCC) 設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            聯合信用卡中心提供信用卡線上收款服務。請填入從 NCCC 取得的商店資訊。
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="商店代號 (Merchant ID)"
                value={ncccConfig.merchant_id}
                onChange={(e) => setNcccConfig(prev => ({ ...prev, merchant_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="終端機代號 (Terminal ID)"
                value={ncccConfig.terminal_id}
                onChange={(e) => setNcccConfig(prev => ({ ...prev, terminal_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HashKey"
                value={ncccConfig.hash_key}
                onChange={(e) => setNcccConfig(prev => ({ ...prev, hash_key: e.target.value }))}
                type="password"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HashIV"
                value={ncccConfig.hash_iv}
                onChange={(e) => setNcccConfig(prev => ({ ...prev, hash_iv: e.target.value }))}
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ncccConfig.test_mode}
                    onChange={(e) => setNcccConfig(prev => ({ ...prev, test_mode: e.target.checked }))}
                  />
                }
                label="測試模式"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveNcccConfig}
                disabled={saving}
              >
                儲存信用卡設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* LINE Pay 設定 */}
      {activeTab === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>LINE Pay 設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            LINE Pay 讓消費者使用 LINE Pay 錢包或綁定的信用卡付款。
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Channel ID"
                value={linepayConfig.channel_id}
                onChange={(e) => setLinepayConfig(prev => ({ ...prev, channel_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Channel Secret"
                value={linepayConfig.channel_secret}
                onChange={(e) => setLinepayConfig(prev => ({ ...prev, channel_secret: e.target.value }))}
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={linepayConfig.test_mode}
                    onChange={(e) => setLinepayConfig(prev => ({ ...prev, test_mode: e.target.checked }))}
                  />
                }
                label="測試模式 (使用 Sandbox)"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveLinepayConfig}
                disabled={saving}
              >
                儲存 LINE Pay 設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 運費設定 */}
      {activeTab === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>運費設定</Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="基本運費"
                type="number"
                value={shippingConfig.base_fee}
                onChange={(e) => setShippingConfig(prev => ({ ...prev, base_fee: parseInt(e.target.value) || 0 }))}
                InputProps={{ startAdornment: 'NT$' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="免運門檻"
                type="number"
                value={shippingConfig.free_shipping_threshold}
                onChange={(e) => setShippingConfig(prev => ({ ...prev, free_shipping_threshold: parseInt(e.target.value) || 0 }))}
                InputProps={{ startAdornment: 'NT$' }}
                helperText="設為 0 表示不提供免運"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveShippingConfig}
                disabled={saving}
              >
                儲存運費設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 行銷追蹤設定 */}
      {activeTab === 6 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>行銷追蹤設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            設定各項追蹤代碼以進行廣告成效分析與再行銷。
          </Alert>

          <Grid container spacing={3}>
            {/* Google 追蹤 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Google 追蹤
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Tag Manager ID"
                value={marketingConfig.gtm_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, gtm_id: e.target.value }))}
                placeholder="GTM-XXXXXXX"
                helperText="例如: GTM-ABCD123"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Analytics 4 ID"
                value={marketingConfig.ga4_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, ga4_id: e.target.value }))}
                placeholder="G-XXXXXXXXXX"
                helperText="例如: G-W3HW6DQ3GQ"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Ads 轉換 ID"
                value={marketingConfig.google_ads_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, google_ads_id: e.target.value }))}
                placeholder="AW-XXXXXXXXXX"
                helperText="例如: AW-123456789"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Ads 轉換標籤"
                value={marketingConfig.google_ads_label}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, google_ads_label: e.target.value }))}
                placeholder="轉換標籤"
                helperText="用於追蹤特定轉換事件"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Search Console 驗證碼"
                value={marketingConfig.google_search_console}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, google_search_console: e.target.value }))}
                placeholder="驗證碼或 HTML 標籤"
                helperText="用於驗證網站擁有權"
              />
            </Grid>

            {/* Facebook 追蹤 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Facebook 追蹤
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facebook Pixel ID"
                value={marketingConfig.fb_pixel_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, fb_pixel_id: e.target.value }))}
                placeholder="1234567890123456"
                helperText="16 位數的 Pixel ID"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facebook Access Token"
                value={marketingConfig.fb_access_token}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, fb_access_token: e.target.value }))}
                type="password"
                helperText="Conversions API 用，可選填"
              />
            </Grid>

            {/* TikTok 追蹤 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                🎵 TikTok 抖音追蹤
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TikTok Pixel ID"
                value={marketingConfig.tiktok_pixel_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_pixel_id: e.target.value }))}
                placeholder="CXXXXXXXXXXXXXXXXX"
                helperText="從 TikTok Ads Manager 取得"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TikTok Access Token"
                value={marketingConfig.tiktok_access_token}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_access_token: e.target.value }))}
                type="password"
                helperText="Events API 用，可選填"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketingConfig.tiktok_test_mode}
                    onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_test_mode: e.target.checked }))}
                  />
                }
                label="TikTok 測試模式"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                啟用後事件會發送到測試環境，不影響正式廣告數據
              </Typography>
            </Grid>

            {/* Google Merchant Center */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Google Merchant Center
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Merchant Center ID"
                value={marketingConfig.merchant_center_id}
                onChange={(e) => setMarketingConfig(prev => ({ ...prev, merchant_center_id: e.target.value }))}
                placeholder="123456789"
                helperText="Google Merchant Center 商家 ID"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={marketingConfig.merchant_feed_enabled}
                    onChange={(e) => setMarketingConfig(prev => ({ ...prev, merchant_feed_enabled: e.target.checked }))}
                  />
                }
                label="啟用商品 Feed"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                啟用後可從 /api/settings/merchant-feed 取得商品 XML Feed
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveMarketingConfig}
                disabled={saving}
              >
                儲存行銷追蹤設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 登入設定 */}
      {activeTab === 7 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>登入方式設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            設定會員登入方式，可啟用或停用各種登入選項。
          </Alert>

          <Grid container spacing={3}>
            {/* 手機簡訊登入 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                手機簡訊登入
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={authConfig.phone_enabled}
                    onChange={(e) => setAuthConfig(prev => ({ ...prev, phone_enabled: e.target.checked }))}
                  />
                }
                label="啟用手機簡訊登入"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                使用者透過手機號碼接收驗證碼登入
              </Typography>
            </Grid>

            {/* Google OAuth 登入 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Google OAuth 登入
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={authConfig.google_enabled}
                    onChange={(e) => setAuthConfig(prev => ({ ...prev, google_enabled: e.target.checked }))}
                  />
                }
                label="啟用 Google 登入"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Client ID"
                value={authConfig.google_client_id}
                onChange={(e) => setAuthConfig(prev => ({ ...prev, google_client_id: e.target.value }))}
                placeholder="xxxxx.apps.googleusercontent.com"
                helperText="從 Google Cloud Console 取得"
                disabled={!authConfig.google_enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Google Client Secret"
                value={authConfig.google_client_secret}
                onChange={(e) => setAuthConfig(prev => ({ ...prev, google_client_secret: e.target.value }))}
                type="password"
                helperText="從 Google Cloud Console 取得"
                disabled={!authConfig.google_enabled}
              />
            </Grid>

            {/* LINE Login */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                LINE Login
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={authConfig.line_enabled}
                    onChange={(e) => setAuthConfig(prev => ({ ...prev, line_enabled: e.target.checked }))}
                  />
                }
                label="啟用 LINE 登入"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="LINE Channel ID"
                value={authConfig.line_channel_id}
                onChange={(e) => setAuthConfig(prev => ({ ...prev, line_channel_id: e.target.value }))}
                helperText="從 LINE Developers Console 取得"
                disabled={!authConfig.line_enabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="LINE Channel Secret"
                value={authConfig.line_channel_secret}
                onChange={(e) => setAuthConfig(prev => ({ ...prev, line_channel_secret: e.target.value }))}
                type="password"
                helperText="從 LINE Developers Console 取得"
                disabled={!authConfig.line_enabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LINE Callback URL"
                value={authConfig.line_callback_url}
                onChange={(e) => setAuthConfig(prev => ({ ...prev, line_callback_url: e.target.value }))}
                placeholder="https://yoursite.com/api/auth/line/callback"
                helperText="LINE 登入完成後的回調網址"
                disabled={!authConfig.line_enabled}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveAuthConfig}
                disabled={saving}
              >
                儲存登入設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 商店資訊設定 */}
      {activeTab === 8 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>商店資訊設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            設定商店基本資訊，這些資訊會顯示在前台頁面的頁首、頁尾等位置。
          </Alert>

          <Grid container spacing={3}>
            {/* 基本資訊 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>基本資訊</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="商店名稱" value={storeConfig.store_name}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, store_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="商店簡稱" value={storeConfig.store_name_short}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, store_name_short: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="英文名稱" value={storeConfig.store_name_en}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, store_name_en: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="公司名稱" value={storeConfig.company_name}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, company_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="標語 Slogan" value={storeConfig.slogan}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, slogan: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="商店描述" multiline rows={2} value={storeConfig.description}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, description: e.target.value }))} />
            </Grid>

            {/* 聯絡資訊 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>聯絡資訊</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="電話" value={storeConfig.phone}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, phone: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Email" value={storeConfig.email}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, email: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="LINE ID" value={storeConfig.line_id}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, line_id: e.target.value }))} placeholder="@example" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="服務時間" value={storeConfig.service_hours}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, service_hours: e.target.value }))} placeholder="週一至週五 9:00-18:00" />
            </Grid>

            {/* 圖片設定 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>圖片設定</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* Logo */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Logo</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  建議尺寸: 200x50 px (PNG/SVG)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={storeConfig.logo_url}
                    sx={{ width: 80, height: 40, bgcolor: '#f5f5f5' }}
                  >
                    <ImageIcon />
                  </Avatar>
                  <input
                    type="file"
                    accept="image/*"
                    ref={logoInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(e.target.files[0], 'logo_url')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    上傳
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Logo URL"
                  value={storeConfig.logo_url}
                  onChange={(e) => setStoreConfig(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="/images/logo.png"
                />
              </Card>
            </Grid>

            {/* Favicon */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Favicon</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  建議尺寸: 32x32 px (ICO/PNG)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={storeConfig.favicon_url}
                    sx={{ width: 32, height: 32, bgcolor: '#f5f5f5' }}
                  >
                    <ImageIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <input
                    type="file"
                    accept="image/*,.ico"
                    ref={faviconInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(e.target.files[0], 'favicon_url')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    上傳
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Favicon URL"
                  value={storeConfig.favicon_url}
                  onChange={(e) => setStoreConfig(prev => ({ ...prev, favicon_url: e.target.value }))}
                  placeholder="/favicon.ico"
                />
              </Card>
            </Grid>

            {/* OG Image */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>OG Image (社群分享圖)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  建議尺寸: 1200x630 px (JPG/PNG)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={storeConfig.og_image_url}
                    sx={{ width: 80, height: 42, bgcolor: '#f5f5f5' }}
                  >
                    <ImageIcon />
                  </Avatar>
                  <input
                    type="file"
                    accept="image/*"
                    ref={ogImageInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(e.target.files[0], 'og_image_url')}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => ogImageInputRef.current?.click()}
                  >
                    上傳
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="OG Image URL"
                  value={storeConfig.og_image_url}
                  onChange={(e) => setStoreConfig(prev => ({ ...prev, og_image_url: e.target.value }))}
                  placeholder="/images/og-image.jpg"
                />
              </Card>
            </Grid>

            {/* 社群連結 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>社群連結</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Facebook URL" value={storeConfig.facebook_url}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, facebook_url: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Instagram URL" value={storeConfig.instagram_url}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, instagram_url: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="LINE 官方帳號連結" value={storeConfig.line_url}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, line_url: e.target.value }))} />
            </Grid>

            {/* Footer 統計數字 */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Footer 統計數字</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="庫存數量顯示" value={storeConfig.stats_inventory}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, stats_inventory: e.target.value }))} placeholder="多樣" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="到貨時間顯示" value={storeConfig.stats_delivery}
                onChange={(e) => setStoreConfig(prev => ({ ...prev, stats_delivery: e.target.value }))} placeholder="2-3天" />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveStoreConfig} disabled={saving}>
                儲存商店資訊
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* SEO 設定 */}
      {activeTab === 9 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>SEO 設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            設定網站的 SEO 相關資訊，包含網站標題、描述、關鍵字等。
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="網站名稱" value={seoConfig.site_name}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, site_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="網站 URL" value={seoConfig.site_url}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, site_url: e.target.value }))} placeholder="https://example.com" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="預設標題" value={seoConfig.default_title}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, default_title: e.target.value }))} helperText="顯示在瀏覽器標籤" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="預設描述" multiline rows={2} value={seoConfig.default_description}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, default_description: e.target.value }))} helperText="搜尋結果中顯示的描述" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="預設關鍵字" value={seoConfig.default_keywords}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, default_keywords: e.target.value }))} helperText="用逗號分隔" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Google 網站驗證碼" value={seoConfig.google_verification}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, google_verification: e.target.value }))} helperText="Google Search Console 驗證" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Bing 網站驗證碼" value={seoConfig.bing_verification}
                onChange={(e) => setSeoConfig(prev => ({ ...prev, bing_verification: e.target.value }))} helperText="Bing Webmaster 驗證" />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveSeoConfig} disabled={saving}>
                儲存 SEO 設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 首頁特色設定 */}
      {activeTab === 10 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>首頁特色設定</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            設定首頁顯示的 4 個特色區塊，每個區塊包含圖示、標題和描述。
          </Alert>

          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((num) => {
              const IconComponent = iconMap[featuresConfig[`feature${num}_icon`]] || LocalOfferIcon;
              return (
                <Grid item xs={12} md={6} key={num}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <IconComponent color="primary" sx={{ fontSize: 40 }} />
                        <Typography variant="subtitle1" fontWeight="bold">特色 {num}</Typography>
                      </Box>
                      <TextField fullWidth size="small" label="圖示名稱" value={featuresConfig[`feature${num}_icon`]}
                        onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_icon`]: e.target.value }))}
                        helperText="LocalOffer, LocalShipping, VerifiedUser, Support" sx={{ mb: 2 }} />
                      <TextField fullWidth size="small" label="標題" value={featuresConfig[`feature${num}_title`]}
                        onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_title`]: e.target.value }))} sx={{ mb: 2 }} />
                      <TextField fullWidth size="small" label="描述" value={featuresConfig[`feature${num}_desc`]}
                        onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_desc`]: e.target.value }))} />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveFeaturesConfig} disabled={saving}>
                儲存首頁特色設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

    </Box>
  );
};

export default AdminSettings;
